import React, { ReactNode, useRef, useState } from "react";
import { AppShell, Navbar, Footer as MantineFooter, Button, createStyles, ScrollArea, Group } from "@mantine/core";
import { observer } from "mobx-react";
import { SideBar } from "./side-bar";
import Footer from "./footer";
import { TopMenu } from "./top-menu";
import t from "../stores/language";
import { useLocation } from "react-router-dom";
import ModuleCreator, { ModuleCreateHandle } from "./module-creator";
import theme from "../stores/theme";
import { Icon } from "../custom-components/icons";
import { faAlignLeft, faBars, faPlus } from "@fortawesome/pro-duotone-svg-icons";
import { useQuery, useStore } from "../stores/store";

export const useStyles = createStyles((theme, isOpen?: boolean) => ({
  appshellContainer: {
    main: {
      backgroundColor: "dark",
      position: "fixed",
    },
    header: {
      backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[8] : "white",
    },
  },
  navbarContainer: {
    boxShadow: "rgb(20 19 19 / 23%) 0px 0px 15px",
    clipPath: "inset(0px -15px 0px 0px)",
    overflowX: "hidden",
    overflowY: "hidden",
    backgroundColor: theme.other.arc.colors[0],
    color: "white",
  },
  modules: {
    padding: theme.other.arc.paddings[2],
    fontSize: theme.fontSizes.lg,
    display: "flex",
    alignItems: "center",
    justifyContent: isOpen ? "space-between" : "center",
  },
  sidebar: {
    ".mantine-ScrollArea-scrollbar": {
      backgroundColor: "transparent",
    },
  },
  footerContainer: {
    overflow: "hidden",
    display: "flex",
    justifyContent: "space-between",
  },
  button: {
    backgroundColor: "transparent",
    padding: theme.other.arc.paddings[0],
    "&: not([data-disabled]):hover": {
      backgroundColor: "transparent",
    },
  },
}));
const AppContainer = observer((props: { children: ReactNode; sideMenu?: boolean; showAll?: boolean }) => {
  const { store } = useStore();
  const { pathname } = useLocation();
  const moduleCreateRef = useRef<ModuleCreateHandle>(null);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const { classes } = useStyles(isOpen);
  const { isLoading } = useQuery({
    queryFn: async () => {
      return await store.initialise();
    },
  });
  if (isLoading) return <></>;
  return (
    <React.Fragment>
      <AppShell
        navbar={
          <Navbar width={{ base: props.sideMenu ? (isOpen ? theme.other?.siderBarWidth : 80) : 1 }} className={classes.navbarContainer}>
            <Navbar.Section className={classes.modules}>
              {isOpen ? (
                <Group>
                  <span>{t("Modules")}</span>
                  <Button
                    onClick={() => {
                      moduleCreateRef.current?.show(null);
                    }}
                    size="xs"
                    className={classes.button}
                  >
                    {!pathname.includes("/reports") ? <Icon size="2x" icon={faPlus}></Icon> : null}
                  </Button>
                </Group>
              ) : null}
              <Button size="md" className={classes.button} onClick={() => setIsOpen(!isOpen)}>
                <Icon icon={isOpen ? faAlignLeft : faBars} size="lg" />
              </Button>
            </Navbar.Section>
            <Navbar.Section className={classes.sidebar} grow component={ScrollArea}>
              {props.sideMenu ? <SideBar treeView={isOpen} showAll={props.showAll} onIconClick={() => setIsOpen(true)} /> : null}
            </Navbar.Section>
          </Navbar>
        }
        footer={
          <MantineFooter className={classes.footerContainer} height={36}>
            <Footer />
          </MantineFooter>
        }
        header={<TopMenu />}
        className={classes.appshellContainer}
      >
        {props.children}
        <ModuleCreator ref={moduleCreateRef}></ModuleCreator>
      </AppShell>
      <iframe style={{ display: "none" }} id="downloadFrame" name="downloadFrame" />
    </React.Fragment>
  );
});

export default AppContainer;
